import { useEffect, useRef, useState } from "react";
import type { ChangeEvent, CSSProperties } from "react";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { OverlayPanel } from "primereact/overlaypanel";
import type { PaginatorTemplateOptions } from "primereact/paginator";
import { Toast } from "primereact/toast";

import { getErrorMessage, isAPIError } from "@/core/services";
import { useTableExport } from "@/modules/export";
import { listAllPermissions, updatePermission, usePermissions } from "@/modules/permissions";
import type { Permission } from "@/modules/permissions";
import { ACTION_TOOLTIP_OPTIONS, EDIT_ACTION_TOOLTIP } from "../components/action-tooltip";
import { EmptyState, resolveEmptyStateKind } from "../components/empty-state";
import type { EmptyStateContent } from "../components/empty-state";
import { createExportFeedback, ExportTableDialog } from "../components/export-table-dialog";
import { StateMessage } from "../components/state-message";
import { EditPermissionDialog } from "./components/edit-permission-dialog";
import type { EditPermissionValues } from "./components/edit-permission-dialog";
import styles from "./permissions-screen.module.scss";

interface PermissionColumn {
  field: keyof Pick<Permission, "codigo" | "nome" | "descricao">;
  header: string;
}

const PERMISSION_COLUMNS: PermissionColumn[] = [
  { field: "codigo", header: "Código" },
  { field: "nome", header: "Nome" },
  { field: "descricao", header: "Descrição" },
];

const EXPORT_FILE_NAME = "permissoes";
const EXPORT_TITLE = "Permissões";

const COLUMN_WIDTHS: Record<PermissionColumn["field"], CSSProperties | undefined> = {
  codigo: { minWidth: "10.9375rem" },
  nome: { minWidth: "9.6875rem" },
  descricao: undefined,
};

/** Base sem permissões: a listagem está vazia porque ainda não há o que exibir. */
const EMPTY_STATE: EmptyStateContent = {
  title: "Nenhuma permissão cadastrada",
  description: "Assim que houver permissões no Hub, elas aparecerão nesta listagem.",
};

/** Busca no input sem registros — `ilu-no-search` do Figma. */
const EMPTY_SEARCH_STATE: EmptyStateContent = {
  title: "Nenhuma permissão encontrada para essa busca.",
  description: "Verifique o termo digitado ou tente buscar com outras palavras.",
};

/** MS001 — confirmação da edição concluída. */
export const UPDATE_SUCCESS_MESSAGE = "Permissão atualizada com sucesso.";
export const UPDATE_ERROR_MESSAGE = "Não foi possível atualizar a permissão.";
export const UPDATE_FORBIDDEN_MESSAGE = "Você não tem permissão para editar as permissões do Hub.";

const DEFAULT_ROWS_PER_PAGE = 10;

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

const ROWS_PER_PAGE_DROPDOWN_OPTIONS = ROWS_PER_PAGE_OPTIONS.map((rows) => ({
  label: `${rows} por página`,
  value: rows,
}));

const CURRENT_PAGE_REPORT = "Mostrando {first} a {last} de {totalRecords} registros";

const PAGINATOR_TEMPLATE: PaginatorTemplateOptions = {
  layout:
    "CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown",
  RowsPerPageDropdown: (options) => (
    <Dropdown
      // Mantém a classe do template padrão para o tema alcançar o seletor.
      className="p-paginator-rpp-options"
      panelClassName={styles.rowsPerPagePanel}
      value={options.value}
      options={ROWS_PER_PAGE_DROPDOWN_OPTIONS}
      onChange={options.onChange}
      aria-label="Registros por página"
    />
  ),
};

export function PermissionsScreen() {
  const configPanel = useRef<OverlayPanel>(null);
  const toast = useRef<Toast>(null);

  const [search, setSearch] = useState("");
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(DEFAULT_ROWS_PER_PAGE);
  const [editing, setEditing] = useState<Permission | null>(null);

  const { permissions, total, status, errorMessage, reload, applyPermission } = usePermissions({
    search,
    page: Math.floor(first / rows) + 1,
    pageSize: rows,
  });

  /** AC2 — o arquivo leva o resultado completo da consulta, e não a página em tela. */
  const exportView = useTableExport({
    loadView: async () => {
      const allPermissions = await listAllPermissions({ pesquisa: search });

      return {
        fileName: EXPORT_FILE_NAME,
        title: EXPORT_TITLE,
        columns: PERMISSION_COLUMNS.map((column) => column.header),
        rows: allPermissions.map((permission) =>
          PERMISSION_COLUMNS.map((column) => permission[column.field]),
        ),
      };
    },
    ...createExportFeedback(toast),
  });

  const isLoading = status === "loading";
  const hasSearch = search.trim() !== "";
  const emptyKind = resolveEmptyStateKind(hasSearch, false);
  const emptyContent = emptyKind === "search" ? EMPTY_SEARCH_STATE : EMPTY_STATE;

  // Só depois da consulta concluída: enquanto ela corre, a tabela segue em tela
  // com o indicador de carregamento e os registros da consulta anterior.
  const isEmpty = status === "success" && permissions.length === 0;

  // Um total menor (pesquisa, recarga) pode deixar a página atual fora da faixa.
  useEffect(() => {
    setFirst((current) => (current < total ? current : 0));
  }, [total]);

  function handleSearch(term: string) {
    setSearch(term);
    setFirst(0);
  }

  function handlePage(event: { first: number; rows: number }) {
    setFirst(event.first);
    setRows(event.rows);
  }

  function handleEdit(permission: Permission) {
    setEditing(permission);
  }

  function showUpdateError(error: unknown) {
    const isForbidden = isAPIError(error) && error.status === 403;

    toast.current?.show({
      severity: "error",
      summary: isForbidden ? "Acesso não autorizado" : UPDATE_ERROR_MESSAGE,
      detail: isForbidden ? UPDATE_FORBIDDEN_MESSAGE : getErrorMessage(error),
      life: 5000,
      closable: true,
    });
  }

  /** Nunca rejeita: a falha mantém o modal aberto com os dados digitados. */
  async function handleUpdate(permission: Permission, values: EditPermissionValues) {
    try {
      const updated = await updatePermission({ id: permission.id, ...values });

      // RN02: id e código seguem os da linha; a resposta só refina nome/descrição
      // e pode vir vazia (ex.: 204), caso em que vale o que foi enviado.
      applyPermission({
        ...permission,
        nome: updated?.nome || values.nome,
        descricao: updated?.descricao || values.descricao,
      });
      setEditing(null);

      toast.current?.show({
        severity: "success",
        summary: "Sucesso",
        detail: UPDATE_SUCCESS_MESSAGE,
        life: 3000,
        closable: true,
      });
    } catch (error) {
      showUpdateError(error);
    }
  }

  /** RN001 — o menu ⋮ abre o modal Exportar Tabela. */
  function handleExportView() {
    configPanel.current?.hide();
    exportView.open();
  }

  function renderActions(permission: Permission) {
    return (
      <Button
        type="button"
        className={styles.editButton}
        icon="pi pi-pencil"
        aria-label={`Editar permissão ${permission.nome}`}
        tooltip={EDIT_ACTION_TOOLTIP}
        tooltipOptions={ACTION_TOOLTIP_OPTIONS}
        onClick={() => handleEdit(permission)}
        data-testid={`permissions-edit-${permission.id}`}
        severity="secondary"
        rounded
        text
      />
    );
  }

  if (status === "forbidden") {
    return (
      <section className={styles.container} data-testid="permissions-screen">
        <StateMessage
          icon="pi pi-lock"
          title="Acesso não autorizado"
          description={errorMessage ?? undefined}
          testId="permissions-forbidden"
        />
      </section>
    );
  }

  return (
    <section className={styles.container} data-testid="permissions-screen">
      <Toast ref={toast} position="top-right" />

      <div className={styles.toolbar}>
        <div className={styles.searchField}>
          <label className={styles.srOnly} htmlFor="permissions-search">
            Pesquisar permissões
          </label>
          <InputText
            id="permissions-search"
            className={styles.searchInput}
            type="search"
            value={search}
            placeholder="Buscar por código, nome..."
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              handleSearch(event.currentTarget.value)
            }
            data-testid="permissions-search"
          />
          <i className={`pi pi-search ${styles.searchIcon}`} aria-hidden />
        </div>

        <Button
          type="button"
          className={styles.configButton}
          icon="pi pi-ellipsis-v"
          aria-label="Opções da tabela"
          aria-haspopup
          onClick={(event) => configPanel.current?.toggle(event)}
          data-testid="permissions-table-config"
          severity="secondary"
          outlined
        />

        <OverlayPanel ref={configPanel} className={styles.configPanel} dismissable>
          <div className={styles.configBody} data-testid="permissions-table-config-panel">
            <button
              type="button"
              className={styles.configAction}
              onClick={handleExportView}
              data-testid="permissions-export-table"
            >
              <i className="pi pi-download" aria-hidden />
              Exportar Visualização
            </button>
          </div>
        </OverlayPanel>
      </div>

      {status === "error" ? (
        <StateMessage
          icon="pi pi-exclamation-triangle"
          title="Não foi possível carregar as permissões."
          description={errorMessage ?? undefined}
          testId="permissions-error"
        >
          <Button
            type="button"
            className={styles.retryButton}
            label="Tentar novamente"
            icon="pi pi-refresh"
            onClick={reload}
            data-testid="permissions-retry"
          />
        </StateMessage>
      ) : (
        <div className={styles.tableWrapper}>
          {isLoading ? (
            <span className={styles.srOnly} role="status" data-testid="permissions-loading">
              Carregando permissões...
            </span>
          ) : null}

          {isEmpty ? (
            <EmptyState
              kind={emptyKind}
              content={emptyContent}
              onClear={emptyKind === "search" ? () => handleSearch("") : undefined}
              testId="permissions-empty"
            />
          ) : (
            <DataTable
              className={styles.table}
              value={permissions}
              dataKey="id"
              loading={isLoading}
              tableStyle={{ minWidth: "45rem" }}
              emptyMessage={emptyContent.title}
              lazy
              totalRecords={total}
              paginator={total > 0}
              paginatorClassName={styles.paginator}
              paginatorTemplate={PAGINATOR_TEMPLATE}
              currentPageReportTemplate={CURRENT_PAGE_REPORT}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              first={first}
              rows={rows}
              onPage={handlePage}
              aria-label="Permissões cadastradas"
              data-testid="permissions-table"
            >
              {PERMISSION_COLUMNS.map((column) => (
                <Column
                  key={column.field}
                  field={column.field}
                  header={column.header}
                  style={COLUMN_WIDTHS[column.field]}
                />
              ))}
              <Column
                header="Ações"
                headerClassName={styles.actionsHeader}
                bodyClassName={styles.actionsCell}
                style={{ width: "6.625rem" }}
                body={renderActions}
              />
            </DataTable>
          )}
        </div>
      )}

      {exportView.isOpen ? (
        <ExportTableDialog
          listName="permissões"
          isExporting={exportView.isExporting}
          onExport={exportView.run}
          onCancel={exportView.close}
        />
      ) : null}

      {editing ? (
        // A chave descarta o formulário ao trocar de permissão, sem efeito de sincronização.
        <EditPermissionDialog
          key={editing.id}
          permission={editing}
          onSubmit={(values) => handleUpdate(editing, values)}
          onCancel={() => setEditing(null)}
        />
      ) : null}
    </section>
  );
}
